import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Megaphone, Target, CheckCircle2, Clock, LogIn, LogOut, Timer, BarChart2, RefreshCw, CalendarDays, Activity, FileText, XCircle, Hourglass } from 'lucide-react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './MarketingDashboard.css';
const BreakDropdown = ({ value, onChange, availableOptions = ['Tea Break', 'Lunch Break', 'Other'] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = availableOptions.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #e2e8f0', borderRadius: '8px',
          background: '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}
      >
        <span style={{ color: value ? '#0f172a' : '#94a3b8' }}>{value || 'Select Break Type'}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
      </div>
      {isOpen && (
        <div style={{
          marginTop: '8px',
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.08)', zIndex: 50, padding: '8px'
        }}>
          <input
            type="text"
            placeholder="Search break types..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px', fontSize: '13px', background: '#f8fafc',
              border: '1px solid #f1f5f9', borderRadius: '6px', outline: 'none', marginBottom: '8px'
            }}
          />
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <div
              onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}
              style={{
                padding: '8px 12px', fontSize: '14px', cursor: 'pointer', borderRadius: '6px',
                background: !value ? '#dcfce7' : 'transparent',
                color: !value ? '#166534' : '#334155',
                fontWeight: !value ? 600 : 400
              }}
              onMouseEnter={e => { if (value) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={e => { if (value) e.currentTarget.style.background = 'transparent'; }}
            >
              Select Break Type
            </div>
            {filteredOptions.map(opt => (
              <div
                key={opt}
                onClick={() => { onChange(opt); setIsOpen(false); setSearch(''); }}
                style={{
                  padding: '8px 12px', fontSize: '14px', cursor: 'pointer', borderRadius: '6px',
                  background: value === opt ? '#dcfce7' : 'transparent',
                  color: value === opt ? '#166534' : '#334155',
                  fontWeight: value === opt ? 600 : 400
                }}
                onMouseEnter={e => { if (value !== opt) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (value !== opt) e.currentTarget.style.background = 'transparent'; }}
              >
                {opt}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                No matches found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function MarketingDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState({
    presentDays: 0,
    absentDays: 0,
    halfDays: 0,
    lateMarks: 0
  });
  const [broadcastCount, setBroadcastCount] = useState(0);
  const [goalsCount, setGoalsCount] = useState(0);
  const [goalsList, setGoalsList] = useState([]);
  const [leavesList, setLeavesList] = useState([]);
  const [checkInTimer, setCheckInTimer] = useState(0);
  // --- BREAK LOGIC STATES ---
  const [activeBreak, setActiveBreak] = useState(null);
  const [hideBreakScreen, setHideBreakScreen] = useState(false);
  const [breakTimer, setBreakTimer] = useState(0);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakForm, setBreakForm] = useState({ type: 'Tea Break', reason: '' });
  const [breakDetailsModal, setBreakDetailsModal] = useState({ visible: false, type: '', breaks: [] });
  const [breakLimits, setBreakLimits] = useState({ totalBreakLimit: 60 });

  useEffect(() => {
    api.get('/attendance/break-limits').then(res => {
      if (res.data) {
        setBreakLimits({
          totalBreakLimit: res.data.totalBreakLimit ?? 60
        });
      }
    }).catch(() => { });
  }, []);

  useEffect(() => {
    let interval;
    if (activeBreak && activeBreak.startTime && !activeBreak.endTime) {
      const updateBreakTimer = () => {
        const elapsed = Math.floor((Date.now() - new Date(activeBreak.startTime).getTime()) / 1000);
        setBreakTimer(elapsed);
      };
      updateBreakTimer();
      interval = setInterval(updateBreakTimer, 1000);
    } else {
      setBreakTimer(0);
    }
    return () => clearInterval(interval);
  }, [activeBreak]);

  const checkInIntervalRef = useRef(null);

  useEffect(() => {
    fetchDashboardData();
    return () => {
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
      }
    };
  }, []);

  // Timer logic
  // Rules: only Lunch/Tea breaks pause this timer; Other breaks do not.
  useEffect(() => {
    if (todayAttendance?.loginTime) {
      const updateTimer = () => {
        const endTime = todayAttendance.logoutTime ? new Date(todayAttendance.logoutTime) : new Date();
        let elapsed = Math.floor((endTime - new Date(todayAttendance.loginTime)) / 1000);
        let deductSecs = 0;

        if (todayAttendance.breaks && todayAttendance.breaks.length > 0) {
          todayAttendance.breaks.forEach(b => {
            if (b.breakType === 'Other') return; // ignore Other breaks

            let breakDur = 0;
            if (b.endTime) {
              breakDur = Math.round((new Date(b.endTime) - new Date(b.startTime)) / 1000);
            } else {
              // Active: pause by subtracting full elapsed
              const breakEnd = todayAttendance.logoutTime ? new Date(todayAttendance.logoutTime) : new Date();
              breakDur = Math.round((breakEnd - new Date(b.startTime)) / 1000);
            }
            deductSecs += breakDur;
          });
        }

        setCheckInTimer(Math.max(0, elapsed - deductSecs));
      };

      updateTimer();
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
      }
      if (!todayAttendance.logoutTime) {
        checkInIntervalRef.current = setInterval(updateTimer, 1000);
      }
    } else {
      setCheckInTimer(0);
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
      }
    }
    return () => {
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
      }
    };
  }, [todayAttendance, activeBreak, breakLimits]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [todayRes, statsRes, broadcastsRes, goalsRes, leavesRes] = await Promise.all([
        api.get('/attendance/today').catch(() => ({ data: null })),
        api.get('/attendance/my-stats').catch(() => ({ data: {} })),
        api.get('/broadcasts').catch(() => ({ data: [] })),
        api.get('/goals').catch(() => ({ data: [] })),
        api.get('/leaves').catch(() => ({ data: [] }))
      ]);
      setTodayAttendance(todayRes.data || null);
      if (todayRes.data && todayRes.data.breaks && todayRes.data.breaks.length > 0) {
        const ongoingBreak = todayRes.data.breaks.find(b => !b.endTime);
        setActiveBreak(ongoingBreak || null);
      } else {
        setActiveBreak(null);
      }
      setAttendanceStats({
        presentDays: statsRes.data?.presentDays || 0,
        absentDays: statsRes.data?.absentDays || 0,
        halfDays: statsRes.data?.halfDays || 0,
        lateMarks: statsRes.data?.lateMarks || 0
      });
      setBroadcastCount(Array.isArray(broadcastsRes.data) ? broadcastsRes.data.length : 0);
      const goalsData = Array.isArray(goalsRes.data) ? goalsRes.data : [];
      setGoalsCount(goalsData.length);
      setGoalsList(goalsData);
      setLeavesList(Array.isArray(leavesRes.data) ? leavesRes.data : []);
    } catch (error) {
      console.error('Error fetching marketing dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  const fetchTodayAttendance = async () => {
    try {
      const response = await api.get('/attendance/today');
      if (response.data) {
        setTodayAttendance(response.data);
        if (response.data.breaks && response.data.breaks.length > 0) {
          const ongoingBreak = response.data.breaks.find(b => !b.endTime);
          setActiveBreak(ongoingBreak || null);
        } else {
          setActiveBreak(null);
        }
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setTodayAttendance(null);
    }
  };

  const handleBreakStart = async (e) => {
    setHideBreakScreen(false);
    e.preventDefault();
    if (breakForm.type === 'Other' && !breakForm.reason.trim()) {
      alert('Please enter a reason for your break.');
      return;
    }
    try {
      const response = await api.post('/attendance/break/start', {
        breakType: breakForm.type,
        reason: breakForm.reason
      });
      if (response.data?.break) {
        setActiveBreak(response.data.break);
        setShowBreakModal(false);
        setBreakForm({ type: 'Tea Break', reason: '' });
        await fetchTodayAttendance();
      }
    } catch (error) {
      console.error('Error starting break:', error);
      alert(error.response?.data?.message || 'Failed to start break. Please try again.');
    }
  };

  const handleBreakEnd = async () => {
    try {
      await api.post('/attendance/break/end');
      setActiveBreak(null);
      setBreakTimer(0);
      await fetchTodayAttendance();
      alert('Break ended successfully.');
    } catch (error) {
      console.error('Error ending break:', error);
      alert(error.response?.data?.message || 'Failed to end break. Please try again.');
    }
  };

  const getTotalBreakDurationString = () => {
    let totalSecs = 0;
    if (todayAttendance?.breaks) {
      todayAttendance.breaks.forEach(b => {
        if (b.duration) {
          totalSecs += b.duration * 60;
        } else if (b.startTime && !b.endTime) {
          totalSecs += Math.floor((Date.now() - new Date(b.startTime).getTime()) / 1000);
        }
      });
    }
    if (activeBreak) {
      const inArray = todayAttendance?.breaks?.some(b => b.startTime === activeBreak.startTime);
      if (!inArray) {
        totalSecs += Math.floor((Date.now() - new Date(activeBreak.startTime).getTime()) / 1000);
      }
    }
    if (totalSecs === 0) return '0 mins';
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    if (hrs > 0) return `${hrs} hr ${mins} mins`;
    return `${mins} mins`;
  };
  const handleCheckIn = async () => {
    try {
      if (!navigator.geolocation) {
        alert('Location access is not supported by your browser. Check-in requires location.');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const locationData = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              address: `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`
            };
            const response = await api.post('/attendance/mark', {
              method: 'self-checkin',
              location: locationData
            });
            if (response.data) {
              setTodayAttendance(response.data);
              alert('Check-in successful!');
              fetchDashboardData();
            }
          } catch (error) {
            const msg = error.response?.data?.message || '';
            if (msg.toLowerCase().includes('away') || msg.toLowerCase().includes('office')) {
              alert('You can only check in inside the office.');
            } else {
              alert(msg || 'Failed to check in. Please try again.');
            }
          }
        },
        () => { alert('Please allow location access to complete check-in.'); },
        { timeout: 8000, enableHighAccuracy: true, maximumAge: 0 }
      );
    } catch (error) {
      console.error('Error checking in:', error);
      alert(error.response?.data?.message || 'Failed to check in. Please try again.');
    }
  };

  const handleCheckOut = async () => {
    try {
      if (!navigator.geolocation) {
        alert('Location access is not supported by your browser. Check-out requires location.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            address: `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`
          };
          api.post('/attendance/checkout', { location: locationData })
            .then(response => {
              if (response.data) {
                setTodayAttendance(response.data);
                alert(`Checkout successful! Working Hours: ${response.data.workingHours} hrs | Status: ${response.data.status}`);
                fetchDashboardData();
              }
            })
            .catch(err => {
              const msg = err.response?.data?.message || '';
              if (msg.toLowerCase().includes('away') || msg.toLowerCase().includes('office')) {
                alert('You can only check out inside the office.');
              } else {
                alert(msg || 'Failed to check out. Please try again.');
              }
            });
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Please give permission for location to check out.');
        },
        { timeout: 8000, enableHighAccuracy: true, maximumAge: 0 }
      );
    } catch (error) {
      console.error('Error checking out:', error);
      alert(error.response?.data?.message || 'Failed to check out. Please try again.');
    }
  };

  const formatTime = (time) => {
    if (!time) return '—';
    return new Date(time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatTimer = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const attendanceStatusLabel = useMemo(() => {
    if (!todayAttendance) return 'Not Checked In';
    if (todayAttendance.logoutTime) return 'Checked Out';
    if (todayAttendance.status === 'late-checkin') return 'Late Check-in';
    if (todayAttendance.status === 'early-checkin') return 'Early Check-in';
    return 'Checked In';
  }, [todayAttendance]);

  const attendanceStatusColor = useMemo(() => {
    if (!todayAttendance) return '#6b7280';
    if (todayAttendance.logoutTime) return '#056098';
    if (todayAttendance.status === 'late-checkin') return '#f59e0b';
    if (todayAttendance.status === 'early-checkin') return '#0ea5e9';
    return '#16a34a';
  }, [todayAttendance]);

  const totalDays = attendanceStats.presentDays + attendanceStats.absentDays + attendanceStats.halfDays;

  let lunchCount = 0, teaCount = 0, otherCount = 0;
  if (todayAttendance?.breaks) {
    todayAttendance.breaks.forEach(b => {
      if (b.breakType === 'Lunch Break') lunchCount++;
      else if (b.breakType === 'Tea Break') teaCount++;
      else if (b.breakType === 'Other') otherCount++;
    });
  }

  const availableBreakOptions = [];
  if (teaCount < 2) availableBreakOptions.push('Tea Break');
  if (lunchCount < 1) availableBreakOptions.push('Lunch Break');
  availableBreakOptions.push('Other');

  const cards = [
    {
      key: 'announcements',
      label: 'Announcements',
      value: broadcastCount,
      featured: true,
      iconBg: '#1EA7FF',
      icon: <Megaphone size={14} color="#162a16" />,
      bubbleColor: '#1EA7FF',
      trendText: 'total broadcasts',
    },
    {
      key: 'tasks',
      label: 'Total Tasks',
      value: goalsCount,
      featured: false,
      iconBg: '#056098',
      icon: <Target size={14} color="#fff" />,
      bubbleColor: '#056098',
      trendText: 'goals assigned',
      trendColor: '#056098',
    },
    {
      key: 'present',
      label: 'Present Days',
      value: attendanceStats.presentDays,
      featured: false,
      iconBg: '#16a34a',
      icon: <CheckCircle2 size={14} color="#fff" />,
      bubbleColor: '#16a34a',
      trendText: 'this month',
      trendColor: '#16a34a',
    },
    {
      key: 'absent',
      label: 'Absent Days',
      value: attendanceStats.absentDays,
      featured: false,
      iconBg: '#ef4444',
      icon: <XCircle size={14} color="#fff" />,
      bubbleColor: '#ef4444',
      trendText: 'this month',
      trendColor: '#ef4444',
    },
  ];

  const summaryItems = [
    {
      label: 'Present',
      value: attendanceStats.presentDays,
      color: '#10b981',
      pct: totalDays ? Math.round((attendanceStats.presentDays / totalDays) * 100) : 0,
    },
    {
      label: 'Absent',
      value: attendanceStats.absentDays,
      color: '#ef4444',
      pct: totalDays ? Math.round((attendanceStats.absentDays / totalDays) * 100) : 0,
    },
    {
      label: 'Half Day',
      value: attendanceStats.halfDays,
      color: '#f59e0b',
      pct: totalDays ? Math.round((attendanceStats.halfDays / totalDays) * 100) : 0,
    },
    {
      label: 'Late Marks',
      value: attendanceStats.lateMarks,
      color: '#8b5cf6',
      pct: attendanceStats.presentDays
        ? Math.min(100, Math.round((attendanceStats.lateMarks / attendanceStats.presentDays) * 100))
        : 0,
    },
  ];

  const recentActivity = useMemo(() => {
    const items = [];
    if (todayAttendance?.loginTime) {
      items.push({ id: 'ci', type: 'checkin', label: 'Checked in', sub: new Date(todayAttendance.loginTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }), date: new Date(todayAttendance.loginTime) });
    }
    if (todayAttendance?.logoutTime) {
      items.push({ id: 'co', type: 'checkout', label: 'Checked out', sub: new Date(todayAttendance.logoutTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }), date: new Date(todayAttendance.logoutTime) });
    }
    goalsList.forEach(g => {
      const name = g.goals?.map(t => t.name).join(', ') || 'Task';
      items.push({ id: 'g-' + g._id, type: 'task', label: 'Task created', sub: name.length > 32 ? name.slice(0, 32) + '\u2026' : name, date: new Date(g.createdAt) });
    });
    leavesList.forEach(l => {
      items.push({ id: 'l-' + l._id, type: 'leave', label: `${l.leaveType} applied`, sub: `${new Date(l.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} \u2014 ${l.status}`, date: new Date(l.createdAt) });
    });
    return items.sort((a, b) => b.date - a.date).slice(0, 7);
  }, [todayAttendance, goalsList, leavesList]);

  let totalBreakMins = 0;
  if (breakDetailsModal.breaks) {
    breakDetailsModal.breaks.forEach(b => {
      if (b.breakType !== 'Other') {
        const dur = b.duration !== undefined ? b.duration : Math.max(0, Math.floor((Date.now() - new Date(b.startTime).getTime()) / 60000));
        totalBreakMins += dur;
      }
    });
  }
  const limitMins = breakLimits?.totalBreakLimit || 60;
  const timeLeftMins = Math.max(0, limitMins - totalBreakMins);

  return (
    <div className="page-container marketing-dashboard-root" style={{ padding: '20px 24px', background: '#f4f7f0', minHeight: '100vh' }}>
      <div className="mk-dashboard">

        {/* â”€â”€ Header â”€â”€ */}
        <div className="mk-dashboard-header">
          <div className="mk-dashboard-title">
            <BarChart2 size={18} color="#14532d" />
            <h2>Marketing Dashboard</h2>
          </div>
          <button
            className="mk-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            style={{ boxShadow: 'none' }}
          >
            {refreshing
              ? <span className="mk-skeleton" style={{ width: 62, height: 16, borderRadius: 5, display: 'inline-block' }} />
              : <><RefreshCw size={13} /> Refresh</>}
          </button>
        </div>

        {/* â”€â”€ Outer 2-col layout â”€â”€ */}
        <div className="mk-outer-layout">

          {/* â”€â”€ LEFT COLUMN â”€â”€ */}
          <div className="mk-left-col">

            {/* Stat Cards */}
            <div className="mk-stats-grid">
              {cards.map((card) => (
                <div
                  key={card.key}
                  className={`mk-stat-card${card.featured ? ' mk-stat-card--featured' : ''}`}
                  style={{ '--bubble-color': card.bubbleColor }}
                >
                  <div className="mk-card-top">
                    <span className={`mk-card-label${card.featured ? ' mk-card-label--featured' : ''}`}>
                      {card.label}
                    </span>
                    <span className="mk-card-icon" style={{ background: card.iconBg }}>
                      {card.icon}
                    </span>
                  </div>
                  {loading ? (
                    <>
                      <div className="mk-skeleton" style={{ height: 22, width: '55%', borderRadius: 6, margin: '6px 0 4px' }} />
                      <div className="mk-skeleton" style={{ height: 11, width: '70%', borderRadius: 5 }} />
                    </>
                  ) : (
                    <>
                      <p
                        className="mk-card-value"
                        style={{ color: card.featured ? '#ffffff' : '#111827' }}
                      >
                        {card.value}
                      </p>
                      <div
                        className="mk-card-trend"
                        style={{ color: card.featured ? 'rgba(255,255,255,0.75)' : '#9ca3af' }}
                      >
                        {card.trendText}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Today Attendance Card */}
            <div className="mk-attendance-card">
              <div className="mk-attendance-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CalendarDays size={19} color="#16a34a" style={{ flexShrink: 0 }} />
                  <div>
                    <h3 className="mk-section-title">Today's Attendance</h3>
                    <p className="mk-section-subtitle">
                      {loading ? (
                        <span className="mk-skeleton" style={{ width: 90, height: 20, borderRadius: 20, display: 'inline-block' }} />
                      ) : (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '2px 10px', borderRadius: 20,
                          background: todayAttendance?.logoutTime ? '#d1fae5' : todayAttendance?.loginTime ? '#dcfce7' : '#f3f4f6',
                          color: attendanceStatusColor,
                          fontWeight: 600, fontSize: 11,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: attendanceStatusColor, display: 'inline-block' }} />
                          {attendanceStatusLabel}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Action button */}
                {!loading && (
                  <div>
                    {!todayAttendance?.loginTime ? (
                      <button className="mk-btn mk-btn-checkin" onClick={handleCheckIn}>
                        <LogIn size={14} /> Check In
                      </button>
                    ) : !todayAttendance?.logoutTime ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {(() => {
                          if (!todayAttendance || !todayAttendance.loginTime || todayAttendance.logoutTime) return null;
                          return (
                            <div
                              onClick={() => setBreakDetailsModal({ visible: true, type: 'All Breaks', breaks: todayAttendance.breaks || [] })}
                              style={{
                                background: '#c4ef4e', color: '#1e293b', padding: '0 16px', height: '36px', borderRadius: '999px',
                                fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', boxSizing: 'border-box', cursor: 'pointer', gap: '6px'
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M12 11h4"></path><path d="M12 16h4"></path><path d="M8 11h.01"></path><path d="M8 16h.01"></path></svg> Break Details
                            </div>
                          );
                        })()}
                        {activeBreak ? (
                          <button className="mk-btn mk-btn-checkout" style={{ background: '#fef3c7', color: '#d97706', borderColor: '#fde68a' }} onClick={handleBreakEnd}>
                            <Hourglass size={14} /> Stop Break ({new Date(breakTimer * 1000).toISOString().substr(11, 8)})
                          </button>
                        ) : (
                          <button className="mk-btn mk-btn-checkout" style={{ background: '#fff', color: '#d97706', borderColor: '#fde68a' }} onClick={() => setShowBreakModal(true)}>
                            <Hourglass size={14} /> Take Break
                          </button>
                        )}
                        <button className="mk-btn mk-btn-checkout" onClick={handleCheckOut}>
                          <LogOut size={14} /> Check Out
                        </button>
                      </div>
                    ) : (
                      <button className="mk-btn mk-btn-done" disabled>
                        Day Completed
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Time info grid */}
              <div className="mk-time-grid">
                <div className="mk-time-cell">
                  <span className="mk-time-label">Check-in Time</span>
                  {loading
                    ? <div className="mk-skeleton" style={{ height: 22, width: 80, borderRadius: 5, marginTop: 4 }} />
                    : <b className="mk-time-value">{formatTime(todayAttendance?.loginTime)}</b>}
                </div>
                <div className="mk-time-cell">
                  <span className="mk-time-label">Check-out Time</span>
                  {loading
                    ? <div className="mk-skeleton" style={{ height: 22, width: 80, borderRadius: 5, marginTop: 4 }} />
                    : <b className="mk-time-value">{formatTime(todayAttendance?.logoutTime)}</b>}
                </div>
                <div className="mk-time-cell">
                  <span className="mk-time-label">Working Hours</span>
                  {loading
                    ? <div className="mk-skeleton" style={{ height: 22, width: 80, borderRadius: 5, marginTop: 4 }} />
                    : <b className="mk-time-value">{todayAttendance?.workingHours ? `${todayAttendance.workingHours} hrs` : '—'}</b>}
                </div>
                <div className="mk-time-cell">
                  <span className="mk-time-label">Status</span>
                  {loading
                    ? <div className="mk-skeleton" style={{ height: 22, width: 70, borderRadius: 5, marginTop: 4 }} />
                    : (
                      <span className={`mk-status-badge mk-status-badge--${todayAttendance?.status || 'none'}`}>
                        {todayAttendance?.status
                          ? todayAttendance.status.charAt(0).toUpperCase() + todayAttendance.status.slice(1)
                          : '—'}
                      </span>
                    )}
                </div>
                <div className="mk-time-cell mk-time-cell--timer">
                  <span className="mk-time-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Timer size={12} /> Live Timer
                  </span>
                  {loading
                    ? <div className="mk-skeleton" style={{ height: 22, width: 100, borderRadius: 5, marginTop: 4 }} />
                    : <b className="mk-time-value mk-timer-value">{formatTimer(checkInTimer)}</b>}
                </div>
              </div>
            </div>

            {/* Tasks Table */}
            <div className="mk-attendance-card" style={{ marginTop: 0 }}>
              <div className="mk-attendance-card-header" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Target size={19} color="#056098" style={{ flexShrink: 0 }} />
                  <div>
                    <h3 className="mk-section-title">My Tasks</h3>
                    <p className="mk-section-subtitle">Tasks assigned to you</p>
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#16a34a', padding: '3px 10px', borderRadius: 20, border: '1px solid #bbf7d0' }}>
                  {goalsCount} total
                </span>
              </div>

              {loading ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {['Task', 'Project', 'Date', 'Progress'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(3)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        {[...Array(4)].map((__, j) => (
                          <td key={j} style={{ padding: '10px 12px' }}><div className="mk-skeleton" style={{ height: 10, width: j === 0 ? '70%' : '50%', borderRadius: 4 }} /></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : goalsList.length === 0 ? (
                <div style={{ padding: '28px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No tasks found</div>
              ) : (
                <div className="custom-scrollbar" style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>#</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Task</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Project</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Date</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {goalsList.map((goal, idx) => {
                        const total = goal.goals?.length || 0;
                        const done = goal.goals?.filter(g => g.isCompleted).length || 0;
                        const pct = total ? Math.round((done / total) * 100) : 0;
                        const projectName = goal.projectName || goal.project?.name || '—';
                        const taskNames = goal.goals?.map(g => g.name).join(', ') || '—';
                        return (
                          <tr key={goal._id} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ padding: '10px 12px', color: '#9ca3af', fontSize: 11 }}>{idx + 1}</td>
                            <td style={{ padding: '10px 12px', color: '#111827', fontWeight: 500, maxWidth: 220 }}>
                              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }} title={taskNames}>
                                {taskNames}
                              </div>
                              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{total} sub-task{total !== 1 ? 's' : ''}</div>
                            </td>
                            <td style={{ padding: '10px 12px', color: '#6b7280' }}>{projectName}</td>
                            <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' }}>
                              {goal.createdAt ? new Date(goal.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', minWidth: 110 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1, height: 5, background: '#f3f4f6', borderRadius: 99, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#10b981' : '#1EA7FF', borderRadius: 99, transition: 'width 0.4s' }} />
                                </div>
                                <span style={{ fontSize: 10, fontWeight: 700, color: pct === 100 ? '#10b981' : '#056098', minWidth: 28 }}>{pct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Leaves Table */}
            <div className="mk-attendance-card" style={{ marginTop: 0 }}>
              <div className="mk-attendance-card-header" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CalendarDays size={19} color="#056098" style={{ flexShrink: 0 }} />
                  <div>
                    <h3 className="mk-section-title">My Leaves</h3>
                    <p className="mk-section-subtitle">Your leave history</p>
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#16a34a', padding: '3px 10px', borderRadius: 20, border: '1px solid #bbf7d0' }}>
                  {leavesList.length} total
                </span>
              </div>

              {loading ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {['Type', 'From', 'To', 'Days', 'Status'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(3)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        {[...Array(5)].map((__, j) => (
                          <td key={j} style={{ padding: '10px 12px' }}><div className="mk-skeleton" style={{ height: 10, width: j === 3 ? '30%' : '60%', borderRadius: 4 }} /></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : leavesList.length === 0 ? (
                <div style={{ padding: '28px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No leave records found</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Type</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>From</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>To</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Days</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Reason</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leavesList.map((leave, idx) => {
                        const start = new Date(leave.startDate);
                        const end = new Date(leave.endDate);
                        const days = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
                        const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        const statusColors = {
                          approved: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
                          pending: { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
                          rejected: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' }
                        };
                        const sc = statusColors[leave.status] || statusColors.pending;
                        return (
                          <tr key={leave._id} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ padding: '10px 12px', color: '#111827', fontWeight: 500, whiteSpace: 'nowrap' }}>{leave.leaveType}</td>
                            <td style={{ padding: '10px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmt(start)}</td>
                            <td style={{ padding: '10px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmt(end)}</td>
                            <td style={{ padding: '10px 12px', color: '#111827', fontWeight: 600, textAlign: 'center' }}>{days}</td>
                            <td style={{ padding: '10px 12px', color: '#6b7280', maxWidth: 180 }}>
                              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 170 }} title={leave.reason}>{leave.reason || '—'}</div>
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{ fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.color, padding: '3px 10px', borderRadius: 20, border: `1px solid ${sc.border}`, whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                                {leave.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>{/* end left col */}

          {/* â”€â”€ RIGHT PANEL â”€â”€ */}
          <div className="mk-right-panel">

            {/* Monthly Summary */}
            <div className="mk-right-card">
              <div className="mk-right-card-header">
                <h4 className="mk-right-card-title">Monthly Summary</h4>
                <div className="mk-right-card-dots"><span /><span /><span /></div>
              </div>
              {loading ? (
                <div style={{ marginTop: 6 }}>
                  {[...Array(4)].map((_, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <div className="mk-skeleton" style={{ width: 8, height: 8, borderRadius: '50%', marginRight: 8, flexShrink: 0 }} />
                      <div className="mk-skeleton" style={{ height: 10, flex: 1, borderRadius: 5, marginRight: 8 }} />
                      <div className="mk-skeleton" style={{ width: 24, height: 10, borderRadius: 5 }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mk-summary-list">
                  {summaryItems.map((item) => (
                    <div key={item.label} className="mk-summary-row">
                      <div className="mk-summary-label-wrap">
                        <span className="mk-summary-dot" style={{ background: item.color }} />
                        <span className="mk-summary-label">{item.label}</span>
                      </div>
                      <div className="mk-summary-bar-wrap">
                        <div className="mk-summary-bar" style={{ width: `${item.pct}%`, background: item.color }} />
                      </div>
                      <span className="mk-summary-val">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Profile / Welcome card */}
            <div className="mk-right-card mk-profile-card">
              <div className="mk-right-card-header" style={{ marginBottom: 14 }}>
                <h4 className="mk-right-card-title">My Profile</h4>
                <div className="mk-right-card-dots"><span /><span /><span /></div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div className="mk-avatar">
                  {(user?.firstName || 'U').charAt(0).toUpperCase()}
                </div>
                <p className="mk-profile-name">
                  {user?.firstName} {user?.lastName || ''}
                </p>
                <p className="mk-profile-role">{user?.role?.name || 'Marketing'}</p>
                <div className="mk-profile-divider" />
                <p className="mk-profile-date">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
                <div className="mk-profile-stats">
                  <div>
                    <strong>{attendanceStats.presentDays}</strong>
                    <span>Present</span>
                  </div>
                  <div>
                    <strong>{attendanceStats.absentDays}</strong>
                    <span>Absent</span>
                  </div>
                  <div>
                    <strong>{goalsCount}</strong>
                    <span>Tasks</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="mk-right-card" style={{ flex: 1 }}>
              <div className="mk-right-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Activity size={14} color="#056098" />
                  <h4 className="mk-right-card-title">Recent Activity</h4>
                </div>
                <div className="mk-right-card-dots"><span /><span /><span /></div>
              </div>
              {loading ? (
                <div style={{ marginTop: 4 }}>
                  {[...Array(5)].map((_, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'flex-start' }}>
                      <div className="mk-skeleton" style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div className="mk-skeleton" style={{ height: 10, width: '70%', borderRadius: 4, marginBottom: 5 }} />
                        <div className="mk-skeleton" style={{ height: 8, width: '50%', borderRadius: 4 }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentActivity.length === 0 ? (
                <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>No recent activity</div>
              ) : (
                <div style={{ position: 'relative', maxHeight: '400px', overflowY: 'auto', paddingRight: '10px' }}>
                  <div style={{ position: 'absolute', left: 13, top: 0, bottom: 0, width: 1, background: '#e5e7eb', zIndex: 0 }} />
                  {recentActivity.map((item, idx) => {
                    const iconMap = {
                      checkin: { icon: <LogIn size={13} />, bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' },
                      checkout: { icon: <LogOut size={13} />, bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
                      task: { icon: <Target size={13} />, bg: '#f0fdf4', color: '#056098', border: '#d1fae5' },
                      leave: { icon: <FileText size={13} />, bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
                    };
                    const ic = iconMap[item.type] || iconMap.task;
                    const isToday = item.date.toDateString() === new Date().toDateString();
                    const timeStr = isToday
                      ? item.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                      : item.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    return (
                      <div key={item.id} style={{ display: 'flex', gap: 10, marginBottom: idx < recentActivity.length - 1 ? 14 : 0, alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                        <div style={{ width: 27, height: 27, borderRadius: '50%', background: ic.bg, color: ic.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1.5px solid ${ic.border}` }}>
                          {ic.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>{item.label}</div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.sub}>{item.sub}</div>
                        </div>
                        <div style={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap', marginTop: 3, flexShrink: 0 }}>{timeStr}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>{/* end right panel */}

        </div>{/* end outer layout */}


        {/* Break Selection Modal */}
        {showBreakModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)' }}>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 8px 32px rgba(15,23,42,0.13)', padding: '20px', width: '400px', maxWidth: '90vw' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#0f172a' }}>Take a Break</h3>
              <form onSubmit={handleBreakStart}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>Break Type <span style={{ color: '#ef4444' }}>*</span></label>
                  <select
                    value={breakForm.type}
                    onChange={(e) => setBreakForm({ ...breakForm, type: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', background: '#f8fafc', outline: 'none' }}
                  >
                    <option value="Tea Break">Tea Break</option>
                    <option value="Lunch Break">Lunch Break</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {breakForm.type === 'Other' && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>Reason <span style={{ color: '#ef4444' }}>*</span></label>
                    <textarea
                      rows={3}
                      value={breakForm.reason}
                      onChange={(e) => setBreakForm({ ...breakForm, reason: e.target.value })}
                      placeholder="Enter reason for break"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', background: '#f8fafc', outline: 'none', resize: 'vertical' }}
                      required
                    ></textarea>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                  <button type="button" onClick={() => setShowBreakModal(false)} style={{ padding: '8px 16px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                  <button type="submit" style={{ padding: '8px 16px', border: 'none', background: '#f59e0b', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Start Break</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Break Details Info Modal */}


        {/* Break Details Info Modal */}
        {breakDetailsModal.visible && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)' }} onClick={() => setBreakDetailsModal({ visible: false, type: '', breaks: [] })}>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 8px 32px rgba(15,23,42,0.13)', padding: '24px', width: '400px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: '0', fontSize: '18px', color: '#0f172a' }}>{breakDetailsModal.type} Details</h3>
                <button onClick={() => setBreakDetailsModal({ visible: false, type: '', breaks: [] })} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                  <XCircle size={20} />
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                  <Clock size={24} strokeWidth={1.5} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Time Remaining</span>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Time Taken</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: '20px', color: '#0f172a', fontWeight: 600, letterSpacing: '-0.5px' }}>{timeLeftMins} <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500, letterSpacing: '0' }}>mins</span></span>
                    <span style={{ fontSize: '16px', color: '#475569', fontWeight: 600 }}>{totalBreakMins} <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 500 }}>mins</span></span>
                  </div>
                </div>
              </div>

              {breakDetailsModal.breaks && breakDetailsModal.breaks.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {breakDetailsModal.breaks.map((b, idx) => {
                    const dur = b.duration !== undefined ? b.duration : Math.max(0, Math.floor((Date.now() - new Date(b.startTime).getTime()) / 60000));
                    return (
                      <div key={idx} style={{ padding: '16px', background: '#fff', borderRadius: '12px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: '0 2px 4px rgba(15,23,42,0.02)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: b.endTime ? '#cbd5e1' : '#10b981' }}></div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', marginBottom: '2px' }}>{b.breakType || `Break ${idx + 1}`}</div>
                              <div style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Hourglass size={12} />
                                {new Date(b.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                {' - '}
                                {b.endTime ? new Date(b.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}
                              </div>
                            </div>
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
                            {dur} mins
                          </div>
                        </div>
                        {b.reason && (
                          <div style={{ fontSize: '12px', color: '#475569', padding: '6px 8px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', marginLeft: '20px' }}>
                            <strong style={{ color: '#334155' }}>Reason:</strong> {b.reason}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#64748b', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>No {breakDetailsModal.type}s taken yet.</p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  onClick={() => setBreakDetailsModal({ visible: false, type: '', breaks: [] })}
                  style={{ padding: '8px 16px', border: 'none', background: '#10b981', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active Break Glassmorphism Screen */}
        {activeBreak && !hideBreakScreen && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.4s ease'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '24px', opacity: 0.8 }}>
                <Clock size={40} color="#334155" strokeWidth={1.5} />
              </div>
              <h1 style={{ fontSize: '26px', color: '#0f172a', margin: '0 0 6px 0', fontWeight: 600, letterSpacing: '-0.3px' }}>On Break</h1>
              <p style={{ fontSize: '15px', color: '#475569', margin: '0 0 36px 0', fontWeight: 500 }}>{activeBreak.type}</p>

              <div style={{ fontSize: '64px', fontWeight: '300', color: '#0f172a', fontVariantNumeric: 'tabular-nums', margin: '0 0 44px 0', letterSpacing: '-1.5px' }}>
                {new Date(breakTimer * 1000).toISOString().substr(11, 8)}
              </div>

              <button
                onClick={() => setHideBreakScreen(true)}
                style={{ padding: '12px 40px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)', transition: 'all 0.2s ease' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 158, 11, 0.35)'; e.currentTarget.style.background = '#d97706'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.25)'; e.currentTarget.style.background = '#f59e0b'; }}
              >
                Resume Work
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MarketingDashboard;
